import React, { useState } from 'react';

/**
 * Filter configuration types
 */
export type FilterType = 'search' | 'select' | 'date-range' | 'number-range';

export interface FilterConfig {
  type: FilterType;
  label: string;
  key: string;
  placeholder?: string;
  options?: Array<{ value: string; label: string }>;
  width?: 'sm' | 'md' | 'lg' | 'full';
}

export interface FilterBarProps {
  filters: FilterConfig[];
  values: Record<string, any>;
  onChange: (key: any, value: any) => void;
  onClear: () => void;
  resultsCount?: number;
  collapsible?: boolean;
}

/**
 * FilterBar component - universal filtering component
 *
 * Supports multiple filter types:
 * - search: Text input with debouncing (handled by parent hook)
 * - select: Dropdown with options
 * - date-range: From/To date inputs
 * - number-range: Min/Max number inputs
 *
 * Features:
 * - Responsive grid layout
 * - Collapsible on mobile
 * - Active filter count badge
 * - Clear all button
 * - Results count display
 *
 * @example
 * <FilterBar
 *   filters={filterConfig}
 *   values={filters}
 *   onChange={(key, value) => setFilter(key, value)}
 *   onClear={clearFilters}
 *   resultsCount={totalItems}
 * />
 */
export const FilterBar: React.FC<FilterBarProps> = ({
  filters,
  values,
  onChange,
  onClear,
  resultsCount,
  collapsible = true,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);

  // Count active filters (non-empty values)
  const activeFilterCount = filters.filter((f) => {
    const value = values[f.key];
    if (f.type === 'date-range') {
      return values[`${f.key}From`] || values[`${f.key}To`];
    }
    if (f.type === 'number-range') {
      return values[`${f.key}Min`] !== undefined || values[`${f.key}Max`] !== undefined;
    }
    return value !== '' && value !== undefined && value !== null;
  }).length;

  const widthClasses = {
    sm: 'col-span-1',
    md: 'col-span-1 md:col-span-2',
    lg: 'col-span-1 md:col-span-3',
    full: 'col-span-1 md:col-span-4',
  };

  return (
    <div className="card p-4 mb-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-bold flex items-center gap-2">
            Filters
            {activeFilterCount > 0 && (
              <span className="px-2 py-0.5 text-xs bg-primary-800 text-white rounded-full">
                {activeFilterCount}
              </span>
            )}
          </h3>
          {resultsCount !== undefined && (
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {resultsCount} {resultsCount === 1 ? 'result' : 'results'}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          {activeFilterCount > 0 && (
            <button
              onClick={onClear}
              className="text-sm text-primary-800 hover:text-primary-900 dark:text-primary-400 font-medium"
            >
              Clear all
            </button>
          )}

          {collapsible && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="btn btn-secondary text-sm px-3 py-1.5 md:hidden"
              aria-label={isExpanded ? 'Collapse filters' : 'Expand filters'}
            >
              {isExpanded ? '▲' : '▼'}
            </button>
          )}
        </div>
      </div>

      {/* Filter Grid */}
      {(isExpanded || !collapsible) && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          {filters.map((filter) => (
            <div key={filter.key} className={widthClasses[filter.width || 'md']}>
              {renderFilter(filter, values, onChange)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Render individual filter based on type
 */
function renderFilter(
  filter: FilterConfig,
  values: Record<string, any>,
  onChange: (key: string, value: any) => void
): React.ReactNode {
  switch (filter.type) {
    case 'search':
      return (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {filter.label}
          </label>
          <input
            type="text"
            value={values[filter.key] || ''}
            onChange={(e) => onChange(filter.key, e.target.value)}
            placeholder={filter.placeholder}
            className="input w-full text-sm"
          />
        </div>
      );

    case 'select':
      return (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {filter.label}
          </label>
          <select
            value={values[filter.key] || ''}
            onChange={(e) => onChange(filter.key, e.target.value)}
            className="input w-full text-sm"
          >
            {filter.options?.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      );

    case 'date-range':
      return (
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            {filter.label}
          </label>
          <div className="flex gap-2">
            <div className="flex-1">
              <input
                type="date"
                value={values[`${filter.key}From`] || ''}
                onChange={(e) => onChange(`${filter.key}From`, e.target.value)}
                className="input w-full text-sm"
                placeholder="From"
              />
            </div>
            <div className="flex-1">
              <input
                type="date"
                value={values[`${filter.key}To`] || ''}
                onChange={(e) => onChange(`${filter.key}To`, e.target.value)}
                className="input w-full text-sm"
                placeholder="To"
              />
            </div>
          </div>
        </div>
      );

    case 'number-range':
      return (
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            {filter.label}
          </label>
          <div className="flex gap-2">
            <div className="flex-1">
              <input
                type="number"
                value={values[`${filter.key}Min`] ?? ''}
                onChange={(e) => onChange(`${filter.key}Min`, e.target.value ? Number(e.target.value) : undefined)}
                className="input w-full text-sm"
                placeholder="Min"
              />
            </div>
            <div className="flex-1">
              <input
                type="number"
                value={values[`${filter.key}Max`] ?? ''}
                onChange={(e) => onChange(`${filter.key}Max`, e.target.value ? Number(e.target.value) : undefined)}
                className="input w-full text-sm"
                placeholder="Max"
              />
            </div>
          </div>
        </div>
      );

    default:
      return null;
  }
}
